<template>
  <section class="section video-section">
    <div class="wrap">
      <div class="section-head">
        <div>
          <div class="eyebrow-line">Antes de la carrera</div>
          <h2>VE EL FORMATO<br />EN ACCIÓN.</h2>
        </div>
        <p class="intro-text">
          Dos pruebas piloto. Una en febrero, otra en abril. Mira cómo van las 10 estaciones antes de inscribirte.
        </p>
      </div>
      <div class="video-carousel">
        <div ref="track" class="video-grid" @scroll="onScroll">
          <div
            v-for="v in videos"
            :key="v.id"
            :class="['video-card', { playing: active === v.id }]"
          >
            <div class="video-frame" @click="handleClick(v.id)">
              <video
                :ref="(el) => setRef(el, v.id)"
                :src="v.src"
                preload="metadata"
                playsinline
                muted
                loop
              ></video>
              <button
                v-if="active !== v.id"
                class="play-overlay"
                :aria-label="`Reproducir ${v.title}`"
              >
                <span class="play-circle">
                  <Play :size="28" fill="currentColor" />
                </span>
              </button>
              <div class="video-caption">
                <div class="eyebrow">{{ v.eyebrow }}</div>
                <div class="video-title">{{ v.title }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="carousel-controls">
        <button
          class="carousel-arrow prev"
          aria-label="Vídeo anterior"
          @click="scrollTo(current - 1)"
        >
          <ChevronLeft :size="22" />
        </button>
        <div class="carousel-dots">
          <button
            v-for="i in pageCount"
            :key="i"
            :class="['dot', { active: current === i - 1 }]"
            :aria-label="`Ir al bloque ${i}`"
            @click="scrollTo(i - 1)"
          ></button>
        </div>
        <button
          class="carousel-arrow next"
          aria-label="Vídeo siguiente"
          @click="scrollTo(current + 1)"
        >
          <ChevronRight :size="22" />
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { Play, ChevronLeft, ChevronRight } from 'lucide-vue-next'

const active = ref(null)
const videoEls = ref({})
const track = ref(null)
const current = ref(0)

// Cards shown per "block": 3 on desktop, 1 on mobile. Navigation (arrows +
// dots) steps one block at a time. Kept in sync with the CSS breakpoint below.
const perView = ref(3)
const pageCount = computed(() => Math.ceil(videos.length / perView.value))
let mql = null

function updatePerView() {
  perView.value = mql && mql.matches ? 1 : 3
  // A resize can leave us past the last block — snap back into range.
  current.value = Math.min(current.value, pageCount.value - 1)
}

const videos = [
  {
    id: '3',
    src: 'https://lunikhub.ams3.cdn.digitaloceanspaces.com/hybrid-island-race/videos/pedalea-resiste-compite.mp4',
    eyebrow: '08 · 06 · 2026',
    title: 'Pedalea. Resiste. Compite.',
    desc: 'Primer evento en Gimnàs Ca Nostra. 10 pruebas diferentes, formato test para la carrera de octubre.',
  },
  {
    id: '2',
    src: 'https://lunikhub.ams3.cdn.digitaloceanspaces.com/hybrid-island-race/videos/empieza-el-desafio.mp4',
    eyebrow: '31 · 05 · 2026',
    title: 'Donde empieza el desafío',
    desc: 'Primer evento en Gimnàs Ca Nostra. 10 pruebas diferentes, formato test para la carrera de octubre.',
  },
  {
    id: '1',
    src: 'https://lunikhub.ams3.cdn.digitaloceanspaces.com/hybrid-island-race/videos/una-isla-un-evento.mp4',
    eyebrow: '24 · 05 · 2026',
    title: 'Una isla. Un evento. En un entorno único.',
    desc: 'Una isla. Un evento. En un entorno único.',
  },
  {
    id: 'endurance',
    src: 'https://lunikhub.ams3.cdn.digitaloceanspaces.com/hybrid-island-race/videos/hybrid-endurance-apr2026.mp4',
    eyebrow: '25 · 04 · 2026',
    title: 'Hybrid Endurance',
    desc: 'Una prueba más, gracias a todos los asistentes. Segundo entrenamiento abierto previo a la carrera.',
  },
  {
    id: 'test',
    src: 'https://lunikhub.ams3.cdn.digitaloceanspaces.com/hybrid-island-race/videos/hybrid-test-feb2026.mp4',
    eyebrow: '21 · 02 · 2026',
    title: 'Hybrid Test',
    desc: 'Primer evento en Gimnàs Ca Nostra. 10 pruebas diferentes, formato test para la carrera de octubre.',
  },
  {
    id: 'gym',
    src: 'https://lunikhub.ams3.cdn.digitaloceanspaces.com/hybrid-island-race/videos/gimnas-ca-nostra.mp4',
    eyebrow: 'Coorganiza',
    title: 'Gimnàs Ca Nostra',
    desc: 'Tu fuerza empieza aquí. El gimnasio que coorganiza la Menorca Hybrid Race 2026.',
  },
]

function setRef(el, id) {
  if (el) videoEls.value[id] = el
}

function scrollTo(page) {
  const el = track.value
  if (!el) return
  const clamped = Math.max(0, Math.min(page, pageCount.value - 1))
  const card = el.children[clamped * perView.value]
  if (card) {
    el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: 'smooth' })
  }
  current.value = clamped
}

function onScroll() {
  const el = track.value
  if (!el) return
  const page = Math.round(el.scrollLeft / (el.scrollWidth / pageCount.value))
  current.value = Math.max(0, Math.min(page, pageCount.value - 1))
}

function handleClick(id) {
  active.value = id
}

onMounted(() => {
  mql = window.matchMedia('(max-width: 880px)')
  updatePerView()
  mql.addEventListener('change', updatePerView)
})

onBeforeUnmount(() => {
  if (mql) mql.removeEventListener('change', updatePerView)
})

watch(active, (id) => {
  for (const [vid, el] of Object.entries(videoEls.value)) {
    if (vid === id) {
      el.muted = false
      el.currentTime = 0
      el.play().catch(() => {})
    } else {
      el.pause()
    }
  }
})
</script>

<style scoped>
.intro-text {
  max-width: 460px;
  color: var(--fg-2);
  font-size: 18px;
  margin: 0;
  line-height: 1.55;
}
</style>
